import { Card, Carousel, Tooltip } from "antd";
import React, { useState, useEffect, useRef, memo } from "react";
import { Box, Flex, Text, IconButton } from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { generateSlug, truncateString } from "../../utils/Helper";
import { bucket_base_url, FALLBACK_IMAGE } from "../../constants/constant";
import { CiHeart } from "react-icons/ci";
import { FaHeart, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useDebouncedCallback } from "use-debounce";
import { useMutation } from "@tanstack/react-query";
import { disLikeProperty, likeProperty } from "../../api/Property/propertyApi";
import { setLoginModalVisible } from "../../redux/reducers/globalReducer";
import { imageCache } from "../../utils/constant";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

const PropertyCard = ({ item, liked, refetch, sectionId }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profileData } = useSelector((state) => state?.user);
  const auth = JSON.parse(localStorage.getItem("userData"));
  // Declare images first — lazy useState initialisers below depend on it
  const images = item?.file;
  const imagesLoadedRef = useRef(0);
  const desiredLoadCount = Math.min(images?.length || 0, 10);

  // Start autoplay immediately if ALL first images are already cached
  const [canAutoplay, setCanAutoplay] = useState(() => {
    if (!images || images.length === 0) return false;
    const count = Math.min(images.length, 10);
    let cached = 0;
    for (let i = 0; i < count; i++) {
      const url = images[i]?.filePath?.includes("http")
        ? images[i].filePath
        : `${bucket_base_url}${images[i]?.filePath}`;
      if (imageCache.has(url)) cached++;
    }
    return cached >= count;
  });

  // Synchronously pre-mark any image already in the in-memory cache
  // so it starts as visible and never flickers on navigation.
  const [loadedImages, setLoadedImages] = useState(() => {
    const initial = {};
    (images || []).slice(0, 10).forEach((image, index) => {
      const url = image?.filePath?.includes("http")
        ? image.filePath
        : `${bucket_base_url}${image?.filePath}`;
      if (imageCache.has(url)) {
        initial[index] = true;
      }
    });
    return initial;
  });

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  const DotLoading = () => (
    <Flex
      h="200px"
      w="100%"
      align="center"
      justify="center"
      bg="#f8f8f8"
      gap={1.5}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="6px"
          h="6px"
          bg="#E5BE54"
          borderRadius="full"
          style={{
            animation: `pulse 1.2s infinite ease-in-out ${i * 0.2}s`,
          }}
        />
      ))}
      <style>
        {`
          @keyframes pulse {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
            40% { transform: scale(1.2); opacity: 1; }
          }
        `}
      </style>
    </Flex>
  );

  const { mutate: mutateLike } = useMutation({
    mutationFn: (data) => likeProperty(data),
  });
  const { mutate: mutateDislike } = useMutation({
    mutationFn: () => disLikeProperty(liked[0]?.id),
  });
  const [like, setLike] = useState(liked?.length > 0 ? true : false);
  const handleLike = useDebouncedCallback(() => {
    if (auth) {
      setLike((prev) => !prev);
      if (like) {
        mutateDislike("", {
          onSuccess: () => {
            refetch();
          },
        });
      } else {
        const body = {
          propertyId: item.id,
        };
        mutateLike(body, {
          onSuccess: () => {
            refetch();
          },
        });
      }
    } else {
      dispatch(setLoginModalVisible(true));
    }
  }, 300);

  useEffect(() => {
    imagesLoadedRef.current = 0;
    setCanAutoplay(false);
  }, [item?.id]);

  useEffect(() => {
    if (!images || images.length === 0) return;

    const preloadImages = () => {
      images?.slice(0, 10).forEach((image) => {
        const imageUrl = image?.filePath?.includes("http")
          ? image.filePath
          : `${bucket_base_url}${image?.filePath}`;

        const handleImageLoad = () => {
          if (!imageCache.has(imageUrl)) {
            imageCache.set(imageUrl, true);
          }
          requestAnimationFrame(() => {
            imagesLoadedRef.current += 1;
            checkIfEnoughLoaded();
          });
        };

        if (!imageCache.has(imageUrl)) {
          const img = new Image();
          img.src = imageUrl;
          img.onload = handleImageLoad;
          img.onerror = handleImageLoad;
        } else {
          handleImageLoad();
        }
      });
    };

    const checkIfEnoughLoaded = () => {
      if (imagesLoadedRef.current >= desiredLoadCount && !canAutoplay) {
        setCanAutoplay(true);
      }
    };

    preloadImages();

    const fallbackTimeout = setTimeout(() => {
      if (!canAutoplay) {
        setCanAutoplay(true);
      }
    }, 3000);

    return () => clearTimeout(fallbackTimeout);
  }, [images, desiredLoadCount, canAutoplay]);

  return (
    <Box
      className="card"
      bg="transparent"
      borderRadius="12px"
      overflow="hidden"
      border="none"
      transition="all 0.3s ease"
      cursor="pointer"
      width="100%"
      onClick={() => {
        // Save current section to Home page state before leaving
        if (sectionId) {
          navigate(location.pathname + location.search, {
            replace: true,
            state: { fromSection: sectionId },
          });
        }
        
        // Ensure slug is never empty for navigation
        const title = intl.locale === "ar" ? item?.title_ar : item?.title_en;
        const slug = generateSlug(title || item?.title_en || "property");
        
        // Then navigate to details
        navigate(
          `/property-details/${item?.id}-${slug}`,
          {
            state: profileData,
          }
        );
      }}
    >
      <Box position="relative">
        <Carousel
          arrows
          dots={false}
          autoplay={canAutoplay}
          autoplaySpeed={6000}
          prevArrow={
            <div className="custom-slick-arrow slick-prev">
              <IconButton
                aria-label="Previous"
                icon={<FaArrowLeft />}
                variant="solid"
                bg="rgba(255, 252, 245, 0.8)"
                color="#e5be54"
                borderRadius="full"
                size="sm"
                _hover={{ bg: "#e5be54", color: "white" }}
                boxShadow="0 4px 10px rgba(0,0,0,0.1)"
              />
            </div>
          }
          nextArrow={
            <div className="custom-slick-arrow slick-next">
              <IconButton
                aria-label="Next"
                icon={<FaArrowRight />}
                variant="solid"
                bg="rgba(255, 252, 245, 0.8)"
                color="#e5be54"
                borderRadius="full"
                size="sm"
                _hover={{ bg: "#e5be54", color: "white" }}
                boxShadow="0 4px 10px rgba(0,0,0,0.1)"
              />
            </div>
          }
        >
          {images && images.length > 0 ? (
            images?.map((image, index) => (
              <Box key={index} h="200px" overflow="hidden" position="relative" sx={{ 
                "&:hover .custom-slick-arrow": { opacity: 1 },
                ".custom-slick-arrow": { 
                  opacity: { base: 1, md: 0 },
                  transition: "opacity 0.2s",
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  cursor: "pointer"
                },
                ".slick-prev": { left: "10px" },
                ".slick-next": { right: "10px" }
              }}>
                {!loadedImages[index] && <DotLoading />}
                <img
                  src={
                    image?.filePath?.includes("http")
                      ? image.filePath
                      : `${bucket_base_url}${image?.filePath}`
                  }
                  alt="propertyImage"
                  loading={index >= 10 ? "lazy" : "eager"}
                  onLoad={() => handleImageLoad(index)}
                  onError={(e) => {
                    handleImageLoad(index);
                    e.target.onerror = null;
                    e.target.src = FALLBACK_IMAGE;
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: loadedImages[index] ? "block" : "none",
                  }}
                />
              </Box>
            ))
          ) : (
            <Box h="200px" overflow="hidden">
              <img
                src={FALLBACK_IMAGE}
                alt="No image available"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}
        </Carousel>

        {item?.isFeatured && (
          <Flex
            position="absolute"
            top="14px"
            left="14px"
            bg="#E5BE54"
            color="white"
            py="7px"
            px="16px"
            borderRadius="full"
            fontSize="10px"
            fontWeight="900"
            zIndex={2}
            align="center"
            gap="6px"
            textTransform="uppercase"
            letterSpacing="1px"
            boxShadow="0 4px 15px rgba(0,0,0,0.15)"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 0L6.12257 3.45492H9.75528L6.81636 5.59017L7.93893 9.04508L5 6.90983L2.06107 9.04508L3.18364 5.59017L0.244718 3.45492H3.87743L5 0Z" fill="white" />
            </svg>
            FEATURED
          </Flex>
        )}

        <Flex
          position="absolute"
          top="14px"
          right="14px"
          zIndex={2}
          bg="rgba(255, 255, 255, 0.4)"
          backdropFilter="blur(8px)"
          w="36px"
          h="36px"
          borderRadius="full"
          align="center"
          justify="center"
          transition="all 0.2s"
          _hover={{ bg: "white", transform: "scale(1.1)" }}
          onClick={(e) => {
            e.stopPropagation();
            handleLike();
          }}
        >
          {like ? <FaHeart color="#FF4D4F" size="18px" /> : <CiHeart color="#1A1A1A" size="22px" />}
        </Flex>
      </Box>

      <Box p={5} pl={1}>
        <Text fontSize="20px" fontWeight="700" color="#1A1A1A" mb={1}>
          {intl.formatMessage(
            { id: "aed", defaultMessage: "{price} AED" },
            { price: item?.price?.toLocaleString() }
          )}
        </Text>
        <Tooltip title={intl.locale === "ar" ? item?.title_ar : item?.title_en}>
          <Text
            fontSize="18px"
            fontWeight="700"
            color="#2D2D2D"
            mb={1}
            noOfLines={1}
          >
            {intl.locale === "ar" ? item?.title_ar : item?.title_en}
          </Text>
        </Tooltip>
        <Text fontSize="14px" color="#666" fontWeight="400" mb={4}>
          {item?.community}, {item?.city}
        </Text>

        <Flex gap={4} align="center">
          <Flex align="center" gap={1.5}>
            <img src="/icons/bed.svg" alt="beds" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
            <Text fontSize="13px" color="#333" fontWeight="500">
              {intl.formatMessage(
                { id: "beds", defaultMessage: "{count} Beds" },
                { count: item?.bedroom }
              )}
            </Text>
          </Flex>
          <Flex align="center" gap={1.5}>
            <img src="/icons/shower.svg" alt="baths" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
            <Text fontSize="13px" color="#333" fontWeight="500">
              {intl.formatMessage(
                { id: "baths", defaultMessage: "{count} Baths" },
                { count: item?.bathroom }
              )}
            </Text>
          </Flex>
          <Flex align="center" gap={1.5}>
            <img src="/icons/area.svg" alt="sqft" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
            <Text fontSize="13px" color="#333" fontWeight="500">
              {intl.formatMessage(
                { id: "sqft", defaultMessage: "{size} SQFT" },
                { size: item?.size }
              )}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default memo(PropertyCard);
